import { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useLocation } from "react-router-dom";

import { CommercialOnboardingPlanStep } from "@/components/commercial-plan-selector";
import { getLearnMoreHrefWithPrice } from "@/components/modal/proactive-upsell/learn-more-href";
import { PROACTIVE_MONEY_BACK_GUARANTEE_NOTE } from "@/components/modal/proactive-upsell/proactive-plan";
import { startProactiveCheckout } from "@/components/modal/proactive-upsell/proactive-checkout";
import { ProactiveTrialPitch } from "@/components/proactive-trial/proactive-trial-pitch";
import { subscriptionModule } from "@/modules/subscriptions";
import { StripeCheckoutSource } from "@/modules/subscriptions/query-params";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard";
import {
  FooterSkipButton,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import { APP_CONFIG } from "@/domains/global/config";
import { buildProactivePaywallEventPayload } from "@/domains/metrics/monetization";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { prepareFloatingMemForOnboarding } from "@/modules/mem-agent/prepare-floating-mem-for-onboarding";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import {
  CommercialPlanKey,
  getCompatibleCommercialCatalog,
  isCommercialRolloutActive,
  type CommercialPlanDefinition,
} from "@/modules/commercial-plans";
import {
  NON_SKIPPABLE_TRIAL_CLIENT_FALLBACK_SOURCE,
  PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE,
} from "@/store/subscriptions";

const PROACTIVE_TRIAL_ONBOARDING_HEADING = "Mem Agent is better with Proactive";

export const ProactiveTrialStep = observer((_props: StepRenderProps) => {
  const { store } = useAppStore();
  const location = useLocation();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const didTrackPaywallView = useRef(false);
  const didTrackProactivePricingExposure = useRef(false);
  const didTrackNonSkippableTrialExposure = useRef(false);

  const isTrialEligible = store.subscriptions.isProactiveUpsellTrialEligible;
  const isNonSkippableTrialEligible = store.subscriptions.isProactiveTrialEligible;
  const nonSkippableTrialAssignment = store.subscriptions.nonSkippableTrialAssignment;
  const nonSkippableTrialAssignmentIsLoading =
    isNonSkippableTrialEligible &&
    (store.subscriptions.nonSkippableTrialAssignmentIsLoading ||
      nonSkippableTrialAssignment === undefined);
  const canSkip =
    !nonSkippableTrialAssignmentIsLoading &&
    (!isNonSkippableTrialEligible || nonSkippableTrialAssignment?.can_skip === true);
  const proactivePricingAssignment = store.subscriptions.proactivePricingAssignment;
  const proactivePricingError = store.subscriptions.proactivePricingAssignmentError;
  const proactivePricingIsLoading =
    store.subscriptions.proactivePricingAssignmentIsLoading ||
    (!proactivePricingAssignment && proactivePricingError === undefined);
  const proactivePrice = proactivePricingAssignment?.display_price;
  const shouldActivateMemAgentOnboarding = store.floatingAgent.isOnboardingSurfaceAvailable;
  const learnMoreHref = getLearnMoreHrefWithPrice({
    learnMoreHref: APP_CONFIG.URLS.MEM_AGENT_DOCS,
    proactivePriceCents: proactivePricingAssignment?.display_price_cents,
  });
  const checkoutReturnConfirmationIsPending =
    store.onboarding.proactiveCheckoutReturnConfirmationIsPending;
  const checkoutResetKey = store.subscriptions.proactiveCheckoutResetKey;
  const commercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
  const commercialCatalog = getCompatibleCommercialCatalog({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });
  const commercialRolloutIsActive = isCommercialRolloutActive({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: commercialPlanSnapshot,
  });

  useEffect(() => {
    if (commercialRolloutIsActive) {
      return;
    }

    void store.subscriptions.preloadProactivePricingAssignment().catch(() => undefined);
  }, [commercialRolloutIsActive, store]);

  useEffect(() => {
    setIsSubscribing(false);
  }, [checkoutResetKey]);

  useEffect(() => {
    if (commercialRolloutIsActive || !proactivePricingAssignment) {
      return;
    }

    if (
      !didTrackProactivePricingExposure.current &&
      proactivePricingAssignment.source !== PROACTIVE_PRICING_CLIENT_FALLBACK_SOURCE
    ) {
      didTrackProactivePricingExposure.current = true;
      trackEvent(TrackedEvent.ExperimentStarted, {
        experiment: proactivePricingAssignment.experiment_key,
        variant: proactivePricingAssignment.variant_key,
      });
    }

    if (!didTrackPaywallView.current) {
      didTrackPaywallView.current = true;
      trackEvent(
        TrackedEvent.PaywallView,
        buildProactivePaywallEventPayload({ store, source: "onboarding" })
      );
    }
  }, [commercialRolloutIsActive, proactivePricingAssignment, store]);

  useEffect(() => {
    if (commercialRolloutIsActive || !isNonSkippableTrialEligible) return;

    let canceled = false;
    void store.subscriptions.ensureNonSkippableTrialAssignment().then(assignment => {
      if (
        canceled ||
        didTrackNonSkippableTrialExposure.current ||
        assignment.source === NON_SKIPPABLE_TRIAL_CLIENT_FALLBACK_SOURCE
      ) {
        return;
      }

      didTrackNonSkippableTrialExposure.current = true;
      trackEvent(TrackedEvent.ExperimentStarted, {
        experiment: assignment.experiment_key,
        variant: assignment.variant_key,
      });
    });

    return () => {
      canceled = true;
    };
  }, [commercialRolloutIsActive, isNonSkippableTrialEligible, store.subscriptions]);

  const retryPricingAssignment = useCallback(() => {
    void store.subscriptions.fetchProactivePricingAssignment({ trackExposure: false });
  }, [store.subscriptions]);

  const handleLearnMore = useCallback(() => {
    void windowModule.openInNewTab({ url: new URL(learnMoreHref) });
  }, [learnMoreHref]);

  const handleSkip = useCallback(async () => {
    trackEvent(
      TrackedEvent.PaywallCancelClick,
      buildProactivePaywallEventPayload({ store, source: "onboarding" })
    );
    if (shouldActivateMemAgentOnboarding) {
      const preparedFloatingMem = await prepareFloatingMemForOnboarding({ store });
      if (!preparedFloatingMem) return;
      store.platformInfo.perDeviceOnboardingInfo.activateMemAgentOnboarding();
    }
    // Mem Agent activation can reveal the next onboarding step immediately, so
    // complete the trial itself instead of whichever step is current afterward.
    store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
  }, [shouldActivateMemAgentOnboarding, store]);

  const handleSubscribe = useCallback(async () => {
    if (isSubscribing || checkoutReturnConfirmationIsPending) return;

    if (store.subscriptions.shouldUseAppleProactiveUpgradeModal) {
      store.subscriptions.openProactiveUpsell();
      return;
    }

    trackEvent(
      TrackedEvent.PaywallUpgradeClick,
      buildProactivePaywallEventPayload({ store, source: "onboarding" })
    );
    setIsSubscribing(true);

    try {
      const checkoutResult = await startProactiveCheckout({
        checkoutSource: StripeCheckoutSource.Onboarding,
        location,
        store,
      });

      if (checkoutResult.didStartExternalNavigation) {
        // The Stripe return handler completes this step only after confirmed
        // Proactive access. Keep users here while the browser leaves for Stripe.
        return;
      }

      if (store.floatingAgent.isOnboardingSurfaceAvailable) {
        const preparedFloatingMem = await prepareFloatingMemForOnboarding({ store });
        if (!preparedFloatingMem) {
          setIsSubscribing(false);
          return;
        }
        store.platformInfo.perDeviceOnboardingInfo.activateMemAgentOnboarding();
      }
      // Subscription reconciliation can reveal the Mem Agent steps before the
      // checkout request resolves. Complete the original trial explicitly so
      // we never complete whichever onboarding step became current meanwhile.
      store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
    } catch (error) {
      trackEvent(TrackedEvent.PaywallUpgradeFailed, {
        ...buildProactivePaywallEventPayload({ store, source: "onboarding" }),
        failure_stage: "checkout_start",
        failure_reason: "checkout_start_error",
        error_message: error instanceof Error ? error.message : "Unknown checkout error",
      });
      logger.error({
        message: "[ProactiveTrialStep] Failed to start proactive checkout",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      toastModule.triggerToast({
        content:
          error instanceof Error
            ? error.message
            : "Failed to start Proactive checkout. Please try again.",
      });
      setIsSubscribing(false);
    }
  }, [checkoutReturnConfirmationIsPending, isSubscribing, location, store]);

  const handleChooseCommercialPlan = useCallback(
    async (plan: CommercialPlanDefinition) => {
      if (
        isSubscribing ||
        checkoutReturnConfirmationIsPending ||
        plan.key === CommercialPlanKey.Mem ||
        !plan.checkoutAvailable
      ) {
        return;
      }

      trackEvent(
        TrackedEvent.PaywallUpgradeClick,
        buildProactivePaywallEventPayload({ store, source: "onboarding" })
      );
      setIsSubscribing(true);

      try {
        const checkoutResult = await subscriptionModule.createCheckoutSessionAndNavigate({
          billingStrategy: BillingStrategyEnum.MONTHLY,
          checkoutSource: StripeCheckoutSource.Onboarding,
          commercialPlanKey: plan.key,
          extraUsageBlocks: 0,
          location,
          store,
        });

        if (!checkoutResult.didStartExternalNavigation) {
          store.platformInfo.perDeviceOnboardingInfo.completeOrSkipProactiveTrialOnboarding();
        }
      } catch (error) {
        logger.error({
          message: "[ProactiveTrialStep] Failed to start commercial checkout",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
        toastModule.triggerToast({
          content: "Failed to start checkout. Please try again.",
        });
        setIsSubscribing(false);
      }
    },
    [checkoutReturnConfirmationIsPending, isSubscribing, location, store]
  );

  if (commercialRolloutIsActive && commercialCatalog && commercialPlanSnapshot) {
    return (
      <WizardContentWrapper>
        <CommercialOnboardingPlanStep
          actionIsLoading={isSubscribing}
          catalog={commercialCatalog}
          onChoosePlan={plan => void handleChooseCommercialPlan(plan)}
          onContinueFree={handleSkip}
          snapshot={commercialPlanSnapshot}
        />
      </WizardContentWrapper>
    );
  }

  return (
    <ProactiveTrialStepView
      canSkip={canSkip}
      isSubscribing={isSubscribing || checkoutReturnConfirmationIsPending}
      isTrialEligible={isTrialEligible}
      nonSkippableTrialAssignmentIsLoading={nonSkippableTrialAssignmentIsLoading}
      proactivePrice={proactivePrice}
      pricingError={proactivePricingError}
      pricingIsLoading={proactivePricingIsLoading}
      onLearnMore={handleLearnMore}
      onRetryPricing={retryPricingAssignment}
      onSkip={handleSkip}
      onSubscribe={handleSubscribe}
    />
  );
});

export interface ProactiveTrialStepViewProps {
  canSkip: boolean;
  isSubscribing?: boolean;
  isTrialEligible: boolean;
  isWindows?: boolean;
  nonSkippableTrialAssignmentIsLoading?: boolean;
  proactivePrice?: string;
  pricingError?: string;
  pricingIsLoading?: boolean;
  onLearnMore: () => void;
  onRetryPricing: () => void;
  onSkip: () => void;
  onSubscribe: () => void;
}

export const ProactiveTrialStepView = ({
  canSkip,
  isSubscribing = false,
  isTrialEligible,
  isWindows,
  nonSkippableTrialAssignmentIsLoading = false,
  proactivePrice,
  pricingError,
  pricingIsLoading = false,
  onLearnMore,
  onRetryPricing,
  onSkip,
  onSubscribe,
}: ProactiveTrialStepViewProps) => {
  return (
    <WizardContentWrapper>
      <ProactiveTrialPitch
        actionNote={PROACTIVE_MONEY_BACK_GUARANTEE_NOTE}
        heading={PROACTIVE_TRIAL_ONBOARDING_HEADING}
        isActionLoading={isSubscribing}
        isTrialEligible={isTrialEligible}
        isWindows={isWindows}
        nonSkippableTrialAssignmentIsLoading={nonSkippableTrialAssignmentIsLoading}
        proactivePrice={proactivePrice}
        pricingError={pricingError}
        pricingIsLoading={pricingIsLoading}
        planFooter={
          canSkip ? (
            <TrialFooter data-proactive-trial-footer>
              <SkipButton onClick={onSkip}>Skip for now</SkipButton>
            </TrialFooter>
          ) : null
        }
        onLearnMore={onLearnMore}
        onRetryPricing={onRetryPricing}
        onSubscribe={onSubscribe}
      />
    </WizardContentWrapper>
  );
};

const TrialFooter = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

const SkipButton = styled(FooterSkipButton)`
  align-self: center;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;
